import React from 'react'

import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'

import { readDelivery } from '../../helpers/guidedEmailDelivery'
import { setGuidedHeader } from '../../helpers/guidedSigningHeader'
import {
  postSigningEvent,
  recordingRequired,
  requestSigningRecording,
} from '../../helpers/signingRecorderBridge'

import {
  PART1_FIRST,
  SELECT_COMPANY_FIRST,
  SigningPart,
  SigningStep,
  canAdoptInto,
  canFinish,
  contractPartLocked,
  describeStep,
  partComplete,
  renderedSigningSteps,
  signAllTargets,
  signatureKind,
  stepsOfPart,
  validSigningEmail,
} from './guidedSigningFlow'

import styles from './GuidedSigning.module.css'

interface Props {
  steps: SigningStep[]
  email: string
  root: React.RefObject<HTMLDivElement | null>
  sendEnabled: boolean
  delivery?: string
  send(email: string, resend?: boolean): Promise<void>
  /** Copies the signature saved in `source` into each of `targets`. */
  adopt?(source: string, targets: string[]): void
  /** No contract company yet: nothing to sign, Start stays disabled. */
  companyMissing?: boolean
  /** Existing legacy agreement: read-only status, never a new Finish send. */
  legacySigned?: boolean
  /** The customer pressed Start (or Sign all at once) to begin signing. */
  onStart?(): void
  /**
   * Signing v2 (phase C, ticket 13213634281): this card covers only one part
   * (`steps` still carries both, for the Part 2 gate and Finish). Never opens
   * a signature by itself: it scrolls to the field and highlights it, and the
   * customer clicks the field. Unset = the single-card flow customers signed
   * before (V67), unchanged.
   */
  part?: SigningPart
}
const HIGHLIGHT = 'guided-current'
const PULSE = 'guided-pulse'
// Two part cards share the page: only one bar guides at a time, and the end
// of Part 1 brings the customer to the Part 2 card.
const START_EVENT = 'guided-signing:start'
const GOTO_EVENT = 'guided-signing:goto'
const announce = (type: string, part: SigningPart) =>
  window.dispatchEvent(new window.CustomEvent(type, { detail: part }))
export const OPEN_DELAY_MS = 250
export const RECORDING_STARTING = 'Starting the signing recording…'
export const RECORDING_REQUIRED =
  'Signing is recorded and cannot start without the recording.'
export const RECORDING_SAVED =
  'The signing recording is saved to this customer under Documentation → Signing recordings.'
export const DONE_FOLD_MS = 3500
const signatureDialogOpen = () => document.body.style.overflow === 'hidden'
// Closes whichever signature dialog is open through its own X (comps/Modal),
// so the contract is visible again.
function closeSignatureDialog() {
  document
    .querySelector<HTMLElement>(
      "[class*='Modal_container'] [class*='Modal_close']",
    )
    ?.click()
}
function clearHighlight(root: HTMLElement | null) {
  root
    ?.querySelectorAll('.' + HIGHLIGHT)
    .forEach((element) => element.classList.remove(HIGHLIGHT, PULSE))
}

const BLUE = '#1976d2'
const GREEN = '#2e7d32'
const ORANGE = '#ed6c02'
const MUTED = '#5f6b75'
const sx = {
  card: {
    background: '#fff',
    border: '1px solid #d3d8dc',
    borderRadius: 12,
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
    padding: '20px 24px',
    margin: '20px auto',
    maxWidth: 820,
  } as React.CSSProperties,
  // v2 part cards: a start block of their own, accent on the left.
  partCard: { borderLeft: '6px solid ' + BLUE } as React.CSSProperties,
  // On top like DocuSign (Jaron 2026-10-05), under the 28px breadcrumbs bar.
  bar: {
    background: '#fff',
    borderBottom: '3px solid ' + BLUE,
    boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
    padding: '10px 20px 8px',
    position: 'fixed',
    left: 0,
    right: 0,
    top: 28,
    // Above the signature dialog overlay (Modal z-index 2) so its buttons stay
    // usable; global.css starts the dialog below this bar while guiding.
    zIndex: 3,
    maxHeight: '50vh',
    overflowY: 'auto',
  } as React.CSSProperties,
  bubble: {
    alignItems: 'center',
    background: BLUE,
    border: 0,
    borderRadius: 999,
    boxShadow: '0 6px 20px rgba(25,118,210,0.45)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    fontSize: 15,
    fontWeight: 600,
    gap: 10,
    padding: '12px 20px',
    position: 'fixed',
    right: 20,
    bottom: 20,
    zIndex: 1,
  } as React.CSSProperties,
  row: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'space-between',
  } as React.CSSProperties,
  eyebrow: {
    color: MUTED,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.6,
    margin: 0,
    textTransform: 'uppercase',
  } as React.CSSProperties,
  stepTitle: { fontSize: 17, fontWeight: 700, margin: '2px 0' },
  description: { color: MUTED, fontSize: 14, margin: '2px 0 0' },
  list: { listStyle: 'none', margin: '12px 0', padding: 0 },
  item: {
    alignItems: 'flex-start',
    borderTop: '1px solid #eef1f3',
    display: 'flex',
    gap: 12,
    padding: '10px 0',
  } as React.CSSProperties,
}

function Dot({ state }: { state: 'done' | 'current' | 'todo' }) {
  return (
    <span
      aria-hidden
      style={{
        alignItems: 'center',
        background:
          state === 'done' ? GREEN : state === 'current' ? ORANGE : '#cfd8dc',
        borderRadius: 999,
        color: '#fff',
        display: 'inline-flex',
        flex: '0 0 22px',
        fontSize: 13,
        fontWeight: 700,
        height: 22,
        justifyContent: 'center',
        marginTop: 1,
      }}
    >
      {state === 'done' ? '✓' : ''}
    </span>
  )
}

function Progress({ steps, next }: { steps: SigningStep[]; next?: string }) {
  return (
    <div style={{ display: 'flex', gap: 4, margin: '8px 0 6px' }}>
      {steps.map((s) => (
        <span
          key={s.field}
          title={s.label}
          style={{
            background: s.signed
              ? GREEN
              : s.field === next
              ? ORANGE
              : '#e0e5e8',
            borderRadius: 3,
            flex: 1,
            height: 6,
          }}
        />
      ))}
    </div>
  )
}

export default function GuidedSigning({
  steps: suppliedSteps,
  email,
  root,
  sendEnabled,
  send,
  adopt,
  delivery = '',
  companyMissing = false,
  legacySigned = false,
  onStart,
  part,
}: Props) {
  const [details, setDetails] = React.useState(false)
  const detailsId = React.useId()
  const detailsButton = React.useRef<HTMLButtonElement>(null)
  const [resend, setResend] = React.useState(false)
  const [sendError, setSendError] = React.useState('')
  let receipt = null
  let receiptError = ''
  try {
    receipt = readDelivery(delivery)
  } catch (e) {
    receiptError = (e as Error).message
  }
  const previouslySent = receipt?.status === 'sent'
  const [started, setStarted] = React.useState(false)
  // 13386544331: inside the iOS app the signing is recorded and Start waits
  // until the recording runs; if it cannot start, signing does not either.
  const [recorder, setRecorder] = React.useState<{
    state: 'idle' | 'starting' | 'failed'
    reason: string
  }>({ reason: '', state: 'idle' })
  const inRecordingApp = recordingRequired()
  const [minimized, setMinimized] = React.useState(false)
  const [reviewSignAll, setReviewSignAll] = React.useState(false)
  const [signAll, setSignAll] = React.useState(false)
  // Field whose drawn image is reused, per kind (signature / initials).
  const [adopted, setAdopted] = React.useState<Record<string, string>>({})
  const [confirm, setConfirm] = React.useState(false)
  const [recipient, setRecipient] = React.useState(email)
  const [status, setStatus] = React.useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle')
  const busy = React.useRef(false)
  const emailInput = React.useRef<HTMLInputElement>(null)
  const [, render] = React.useReducer((version: number) => version + 1, 0)
  React.useEffect(() => {
    const element = root.current
    if (!element) return
    render()
    const observer = new MutationObserver(() => render())
    observer.observe(element, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [root])
  // Pagination renders the contract's signature buttons a few seconds after
  // the report. Show the card right away from the customer's data (it looked
  // missing before) and enable Start once the buttons exist. The Job Summary
  // buttons render unpaginated, so they don't count: wait for the contract's.
  // A contract already fully signed has no buttons left (v2: Part 1 may still
  // be open); without v2 every step is a contract step, as before.
  const contractSupplied = stepsOfPart(suppliedSteps, 'contract')
  const ready =
    !!root.current?.querySelector(
      '[data-guided-signature]:not([data-guided-signature^="job_summary_"])',
    ) ||
    (contractSupplied.length > 0 && contractSupplied.every((s) => s.signed))
  const allSteps = ready
    ? renderedSigningSteps(suppliedSteps, root.current)
    : suppliedSteps
  // This card's steps: all of them, or (v2) only its part's.
  const steps = part ? stepsOfPart(allSteps, part) : allSteps
  const reportCount = stepsOfPart(allSteps, 'report').length
  const contractCount = stepsOfPart(allSteps, 'contract').length
  // Finish (PDF + email, V82) lives in the last part and needs both parts.
  const finishHere = !part || part === 'contract' || !contractCount
  const locked = part === 'contract' && contractPartLocked(allSteps)
  const partLabel =
    part && reportCount && contractCount
      ? 'Part ' + (part === 'report' ? 1 : 2) + ' of 2'
      : ''
  const card = React.useRef<HTMLElement>(null)
  const [cardInView, setCardInView] = React.useState(true)
  const hasSteps = steps.length > 0
  // Sign mode can open scrolled into the contract, past the card: offer a
  // bubble that brings the customer back to it.
  React.useEffect(() => {
    const element = card.current
    if (started || !element || typeof IntersectionObserver === 'undefined')
      return
    const observer = new IntersectionObserver(
      ([entry]) => setCardInView(!!entry?.isIntersecting),
      { threshold: 0.15 },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [started, hasSteps])
  const next = steps.find((s) => !s.signed)
  const complete = canFinish(allSteps)
  const partDone = part ? steps.length > 0 && partComplete(steps) : complete
  const finished =
    complete && (status === 'sent' || (previouslySent && status === 'idle'))
  const done = steps.filter((s) => s.signed).length
  // "Step N of M" names the step being guided to, by its place in the
  // document: a step signed by hand out of order must not shift it.
  const stepNumber = next ? steps.indexOf(next) + 1 : done + 1
  const allTargets = signAllTargets(steps)
  const canSignAll = !!adopt && allTargets.length >= 2
  // Adopted this session, or (after leaving and coming back) any field of the
  // same kind the customer already signed: its saved image is reused.
  const nextKind = next ? signatureKind(next.field) : undefined
  const nextSource =
    adopt && next && nextKind && canAdoptInto(next.field)
      ? adopted[nextKind] ||
        allSteps.find(
          (s) =>
            s.signed &&
            canAdoptInto(s.field) &&
            signatureKind(s.field) === nextKind,
        )?.field
      : undefined
  const placeNext = () => {
    if (adopt && next && nextSource) adopt(nextSource, [next.field])
  }
  const focusNext = React.useCallback(() => {
    clearHighlight(root.current)
    if (!next) return
    const target = Array.from(
      root.current?.querySelectorAll<HTMLElement>(
        '[data-guided-signature="' + next.field + '"]',
      ) || [],
    ).find(
      (element) =>
        element.getClientRects().length > 0 &&
        window.getComputedStyle(element).visibility !== 'hidden',
    )
    if (!target) return
    target.classList.add(HIGHLIGHT)
    if (part) target.classList.add(PULSE)
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    target.focus({ preventScroll: true })
    // Already adopted for this kind: the customer clicks "Sign here" to place
    // it (like DocuSign) — never auto-sign, never reopen the drawing dialog.
    // v2: never open anything; the customer clicks the field and its dialog
    // opens over the contract they are looking at.
    if (nextSource || part) return
    // First of its kind: open the drawing dialog. The contract buttons TOGGLE
    // their dialog, so never click while one is already open (Modal locks
    // body scroll while open). Deferred: Save's close lands first.
    window.setTimeout(() => {
      if (!signatureDialogOpen() && target.isConnected) target.click()
    }, OPEN_DELAY_MS)
    // Keyed on the field, not the `next` object (recreated every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next?.field, nextSource, part, root])
  React.useEffect(() => {
    if (started && !minimized && !confirm) focusNext()
    // Re-run on a new step only; minimizing must not reopen anything.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, next?.field, confirm, focusNext])
  React.useEffect(() => {
    if (!complete) setConfirm(false)
  }, [complete])
  React.useEffect(() => {
    if (complete) clearHighlight(root.current)
  }, [complete, root])
  // Sign all: the first signature drawn is copied to the other eligible ones.
  const signedFields = steps
    .filter((s) => s.signed)
    .map((s) => s.field)
    .join(',')
  const previousSigned = React.useRef(signedFields)
  React.useEffect(() => {
    const before = new Set(previousSigned.current.split(','))
    previousSigned.current = signedFields
    const fresh = signedFields.split(',').find((f) => f && !before.has(f))
    if (!fresh || !adopt) return
    if (canAdoptInto(fresh)) {
      const kind = signatureKind(fresh)
      setAdopted((a) => (a[kind] ? a : { ...a, [kind]: fresh }))
    }
    if (!signAll || !canAdoptInto(fresh)) return
    const targets = signAllTargets(steps).map((s) => s.field)
    setSignAll(false)
    if (targets.length) adopt(fresh, targets)
    // `steps` is derived from signedFields for this purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedFields, signAll, adopt])
  // Tell the signature dialog what is being signed (comps/Signature header).
  const signAllLabels = signAll ? allTargets.map((s) => s.label) : null
  const signAllKey = signAllLabels ? signAllLabels.join('|') : ''
  const nextField = next?.field
  const nextLabel = next?.label
  // v2 has two cards: one that is not guiding must not wipe the other's header.
  const ownsHeader = React.useRef(false)
  React.useEffect(() => {
    if (!started || !nextField || !nextLabel) {
      if (!part || ownsHeader.current) setGuidedHeader(null)
      ownsHeader.current = false
      return
    }
    ownsHeader.current = true
    setGuidedHeader({
      field: nextField,
      title: nextLabel,
      description: describeStep(nextField),
      index: stepNumber,
      total: steps.length,
      signAllLabels: signAllKey ? signAllKey.split('|') : null,
      viewContract: () => setMinimized(true),
      part: partLabel || null,
    })
  }, [
    started,
    nextField,
    nextLabel,
    stepNumber,
    steps.length,
    signAllKey,
    part,
    partLabel,
  ])
  React.useEffect(
    () => () => {
      if (!part || ownsHeader.current) setGuidedHeader(null)
    },
    [part],
  )
  // v2: another part's card started, fold this one; Part 1 ended, show the
  // Part 2 card.
  React.useEffect(() => {
    if (!part) return
    const onStartOther = (e: Event) => {
      if ((e as CustomEvent).detail === part) return
      setStarted(false)
      setMinimized(false)
      setReviewSignAll(false)
      setSignAll(false)
      setDetails(false)
    }
    const onGoto = (e: Event) => {
      if ((e as CustomEvent).detail !== part) return
      card.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    window.addEventListener(START_EVENT, onStartOther)
    window.addEventListener(GOTO_EVENT, onGoto)
    return () => {
      window.removeEventListener(START_EVENT, onStartOther)
      window.removeEventListener(GOTO_EVENT, onGoto)
    }
  }, [part])
  // v2: Part 1's recording ends when its last field is signed, so Part 2
  // gets a recording of its own (13213634281). Said once per completion.
  const part1Posted = React.useRef(false)
  React.useEffect(() => {
    if (part !== 'report') return
    if (!partDone) {
      part1Posted.current = false
      return
    }
    if (!started || part1Posted.current) return
    part1Posted.current = true
    postSigningEvent('signing-part-done', 'report')
  }, [part, partDone, started])
  // Bar fixed on top while guiding: keep the contract's start below it, and
  // let global.css drop the dialog blur so the contract stays readable.
  React.useEffect(() => {
    if (!started) return
    const element = root.current
    const previous = document.body.style.paddingTop
    document.body.style.paddingTop = '120px'
    document.body.classList.add('guided-signing')
    return () => {
      document.body.style.paddingTop = previous
      document.body.classList.remove('guided-signing')
      clearHighlight(element)
    }
  }, [started, root])
  // Clicking the highlighted field in the contract places the adopted
  // signature instead of opening the drawing dialog.
  const nextFieldForClick = next?.field
  React.useEffect(() => {
    const element = root.current
    const field = nextFieldForClick
    if (!started || !element || !field || !nextSource || !adopt) return
    const onClick = (e: MouseEvent) => {
      const hit = (e.target as HTMLElement | null)?.closest?.(
        '[data-guided-signature]',
      )
      if (!hit || hit.getAttribute('data-guided-signature') !== field) return
      e.preventDefault()
      e.stopPropagation()
      adopt(nextSource, [field])
    }
    element.addEventListener('click', onClick, true)
    return () => element.removeEventListener('click', onClick, true)
  }, [started, nextFieldForClick, nextSource, adopt, root])
  React.useEffect(() => {
    if (confirm) emailInput.current?.focus()
  }, [confirm])
  // Done: show the confirmation for a moment, then fold the top bar away
  // (it was left covering the contract after the email went out).
  React.useEffect(() => {
    if (status !== 'sent') return
    const timer = window.setTimeout(() => {
      setStarted(false)
      setConfirm(false)
      setMinimized(false)
    }, DONE_FOLD_MS)
    return () => window.clearTimeout(timer)
  }, [status])
  if (companyMissing) {
    // v2: the Part 1 card (where the single card was) says it once.
    if (part === 'contract') return null
    return (
      <section className="noPDF" aria-label="Guided signing" style={sx.card}>
        <h2 style={{ margin: '4px 0 2px' }}>Sign your agreement</h2>
        <p role="status" style={sx.description}>
          {SELECT_COMPANY_FIRST}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Button variant="contained" size="large" disabled>
            Start
          </Button>
        </div>
      </section>
    )
  }
  if (!steps.length) return null

  const continueSigning = () => {
    setMinimized(false)
    setReviewSignAll(false)
    focusNext()
  }
  const proceed = () => {
    if (part) announce(START_EVENT, part)
    onStart?.()
    setStarted(true)
  }
  const begin = (onFail?: () => void) => {
    setDetails(false)
    if (!inRecordingApp) {
      // Browser / older app build: nothing can record, signing as before.
      // An older app still records each part on its own (13213634281).
      postSigningEvent('signing-start', part ?? null)
      proceed()
      return
    }
    if (recorder.state === 'starting') return
    setRecorder({ reason: '', state: 'starting' })
    void requestSigningRecording(part ?? null).then((reply) => {
      if (reply.status === 'recording') {
        setRecorder({ reason: '', state: 'idle' })
        proceed()
        return
      }
      setRecorder({ reason: reply.reason, state: 'failed' })
      onFail?.()
    })
  }
  const startSignAll = () => {
    setReviewSignAll(false)
    setSignAll(true)
    setMinimized(false)
    if (!started) begin(() => setSignAll(false))
    else focusNext()
  }
  // v2: Part 1 done, fold its bar and bring the customer to the Part 2 card.
  const toPart2 = () => {
    setStarted(false)
    setMinimized(false)
    announce(GOTO_EVENT, 'contract')
  }
  const partNoun = part === 'report' ? 'report' : 'agreement'
  const detailsToggle = (
    <Button
      className={styles['mobileOnly']}
      ref={detailsButton}
      aria-expanded={details || reviewSignAll}
      aria-controls={detailsId}
      onClick={() => {
        setDetails(!(details || reviewSignAll))
        setReviewSignAll(false)
        window.requestAnimationFrame(() => detailsButton.current?.focus())
      }}
    >
      {details || reviewSignAll ? 'Hide details' : 'Details'}
    </Button>
  )
  const bubbleAllowed =
    !part || (part === 'report' ? !partDone : !locked && !finished)

  // v2: the receipt and Resend belong to the card that has Finish.
  const receiptBlock = finishHere && (
    <>
      {receiptError && <p role="alert">{receiptError}</p>}
      {/* While this screen is sending (or just sent) its own status line
          below is the truth; the stored receipt would read as a stale or
          duplicate send ("A previous send is in progress…"). */}
      {receipt && status === 'idle' && !finished && (
        <p role="status">
          {previouslySent
            ? 'Already emailed to ' + receipt.email
            : 'A previous send is in progress or needs confirmation. Check your inbox.'}
        </p>
      )}
      {(receipt || status === 'sent' || status === 'error') &&
        status !== 'sending' &&
        !resend && (
          <Button
            onClick={() => {
              if (part) announce(START_EVENT, part)
              setResend(true)
              setStatus('idle')
              setConfirm(true)
              setStarted(true)
              setMinimized(false)
              setRecipient(email)
            }}
          >
            Resend manually
          </Button>
        )}
    </>
  )

  const signAllReview = reviewSignAll && (
    <div
      role="region"
      aria-label="Sign all at once"
      id={!started ? detailsId : undefined}
      style={{
        background: '#f6f9fc',
        border: '1px solid #dbe6f1',
        borderRadius: 10,
        margin: '10px 0',
        padding: '12px 16px',
      }}
    >
      <p style={{ fontWeight: 700, margin: 0 }}>
        Draw your signature once and it will be placed on:
      </p>
      <ul style={{ ...sx.list, margin: '8px 0' }}>
        {allTargets.map((s) => (
          <li key={s.field} style={{ ...sx.item, padding: '6px 0' }}>
            <Dot state="todo" />
            <span>
              <strong>{s.label}</strong>
              <br />
              <span style={sx.description}>{describeStep(s.field)}</span>
            </span>
          </li>
        ))}
      </ul>
      {steps.some(
        (s) => !s.signed && !allTargets.some((t) => t.field === s.field),
      ) && (
        <p style={sx.description}>
          {steps.some((s) => s.field === 'roof_third_party_sign' && !s.signed)
            ? 'The insurance authorization is signed separately, with its own signature.'
            : 'The second owner still signs separately with their own signature.'}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Button variant="contained" onClick={startSignAll}>
          Draw my signature
        </Button>
        <Button onClick={() => setReviewSignAll(false)}>Cancel</Button>
      </div>
    </div>
  )

  const emailForm = confirm && complete && (
    <form
      onSubmit={async (e) => {
        e.preventDefault()
        if (
          !validSigningEmail(recipient) ||
          !sendEnabled ||
          busy.current ||
          status === 'sent' ||
          (!!receipt && !resend) ||
          !!receiptError ||
          !canFinish(steps)
        )
          return
        busy.current = true
        setStatus('sending')
        setSendError('')
        try {
          await send(recipient.trim(), resend)
          setStatus('sent')
          // A resend that went through offers Resend manually again.
          setResend(false)
          postSigningEvent('signing-finish', part ?? null)
        } catch (e) {
          setSendError((e as Error).message || 'Unable to confirm delivery.')
          setResend(false)
          setStatus('error')
        } finally {
          busy.current = false
        }
      }}
      style={{ marginTop: 10 }}
    >
      <p style={{ fontSize: 17, fontWeight: 600, margin: '4px 0 10px' }}>
        Confirm where we should email your signed agreement.
      </p>
      {resend && (
        <p role="alert">
          This explicitly sends another copy. Check your inbox before
          confirming.
        </p>
      )}
      <TextField
        label="Email address"
        inputRef={emailInput}
        type="email"
        required
        sx={{ marginRight: 1, marginBottom: 1, minWidth: 280 }}
        inputProps={{ style: { fontSize: 17 } }}
        value={recipient}
        disabled={status === 'sending' || status === 'sent'}
        onChange={(e) => setRecipient(e.target.value)}
      />
      <Button
        variant="contained"
        size="large"
        sx={{ marginRight: 1, marginBottom: 1 }}
        type="submit"
        disabled={
          !sendEnabled ||
          !validSigningEmail(recipient) ||
          status === 'sending' ||
          status === 'sent' ||
          (!!receipt && !resend) ||
          !!receiptError
        }
      >
        Confirm and email agreement
      </Button>
      {!sendEnabled && (
        <p role="status">
          Email delivery is not enabled yet. No email has been sent.
        </p>
      )}
      {status === 'sending' && (
        <p role="status">Sending your signed agreement…</p>
      )}
      {status === 'sent' && (
        <p role="status">Your signed agreement has been emailed.</p>
      )}
      {status === 'error' && <p role="alert">{sendError}</p>}
    </form>
  )

  // v2: the contract is "signed" on the card that has Finish; Part 1's card
  // says its own part is done and points to Part 2.
  const shownFinished = finished && finishHere
  const part1Done = !finishHere && partDone

  // All hooks above remain unconditional. Legacy records have no delivery
  // receipt; a signature is not evidence that an email was sent.
  if (legacySigned)
    return (
      <section
        className={'noPDF ' + styles['card']}
        aria-label="Signed agreement"
        style={{ ...sx.card, display: 'block' }}
      >
        <h2 style={{ color: GREEN, margin: 0 }}>Agreement signed ✓</h2>
        <p style={sx.description}>
          This agreement was signed using the previous signing flow.
        </p>
      </section>
    )

  // Before Start: say plainly what will be signed, then offer the two ways.
  if (!started)
    return (
      <>
        {(minimized || !cardInView) && bubbleAllowed && (
          <button
            type="button"
            className="noPDF"
            aria-label="Go to signing"
            style={sx.bubble}
            onClick={() => {
              // Closed with the X (Jaron, Oct 14): bring the card back first.
              setMinimized(false)
              window.requestAnimationFrame(() =>
                card.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                }),
              )
            }}
          >
            <span aria-hidden style={{ fontSize: 18 }}>
              ✍
            </span>
            {'Sign your ' + partNoun + ' · ' + done + ' of ' + steps.length}
          </button>
        )}
        {!minimized && (
          <section
            ref={card}
            className={'noPDF ' + styles['card']}
            data-details={details || reviewSignAll}
            aria-label="Guided signing"
            style={part ? { ...sx.card, ...sx.partCard } : sx.card}
          >
            <Button
              aria-label="Close, sign later"
              className={styles['cardClose']}
              onClick={() => setMinimized(true)}
              title="Not ready to sign: close this card"
            >
              ×
            </Button>
            <p className={styles['count']} style={sx.eyebrow}>
              {partLabel ? partLabel + ' · ' : ''}
              {done} of {steps.length}
              <span className={styles['desktopOnly']}>
                {' '}
                signatures and initials completed
              </span>
            </p>
            {shownFinished ? (
              <>
                <h2
                  className={styles['title']}
                  style={{ color: GREEN, margin: '4px 0 2px' }}
                >
                  Agreement signed ✓
                </h2>
                <p className={styles['fullWidth']} style={sx.description}>
                  {'A signed copy was emailed to ' +
                    (status === 'sent' ? recipient.trim() : receipt?.email) +
                    '.'}
                </p>
                {inRecordingApp && (
                  <p className={styles['fullWidth']} style={sx.description}>
                    {RECORDING_SAVED}
                  </p>
                )}
              </>
            ) : part1Done ? (
              <>
                <h2
                  className={styles['title']}
                  style={{ color: GREEN, margin: '4px 0 2px' }}
                >
                  {complete ? 'All signed ✓' : 'Part 1 complete ✓'}
                </h2>
                {!complete && (
                  <p className={styles['fullWidth']} style={sx.description}>
                    Continue with Part 2 at the start of the agreement.
                  </p>
                )}
              </>
            ) : complete && finishHere ? (
              <>
                <h2
                  className={styles['title']}
                  style={{ color: GREEN, margin: '4px 0 2px' }}
                >
                  All signed ✓
                </h2>
                <p className={styles['detail']} style={sx.description}>
                  Press Finish to email your signed agreement.
                </p>
              </>
            ) : part ? (
              <>
                <h2 className={styles['title']} style={{ margin: '4px 0 2px' }}>
                  {'Sign your ' + partNoun}
                </h2>
                <p className={styles['detail']} style={sx.description}>
                  {'Review the ' +
                    partNoun +
                    ' below. You will be taken to each place that needs your' +
                    ' signature; click the highlighted field to sign it.'}
                </p>
              </>
            ) : (
              <>
                <h2 className={styles['title']} style={{ margin: '4px 0 2px' }}>
                  Sign your agreement
                </h2>
                <p className={styles['detail']} style={sx.description}>
                  Review the agreement below. You will be taken to each place
                  that needs your signature, one at a time.
                </p>
              </>
            )}
            {!reviewSignAll &&
              !shownFinished &&
              !part1Done &&
              !(complete && finishHere) && (
                <ul id={detailsId} className={styles['detail']} style={sx.list}>
                  {steps.map((s) => (
                    <li key={s.field} style={sx.item}>
                      <Dot state={s.signed ? 'done' : 'todo'} />
                      <span>
                        <strong>{s.label}</strong>
                        <br />
                        <span style={sx.description}>
                          {describeStep(s.field)}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            <div className={styles['fullWidth']}>{receiptBlock}</div>
            <div className={styles['fullWidth']}>{signAllReview}</div>
            {part1Done && !complete && (
              <Button
                className={styles['cardContinue']}
                variant="outlined"
                size="large"
                onClick={() => announce(GOTO_EVENT, 'contract')}
              >
                Go to Part 2
              </Button>
            )}
            {reviewSignAll && detailsToggle}
            {locked && !reviewSignAll && (
              <p
                className={styles['fullWidth']}
                role="status"
                style={{ ...sx.description, margin: '0 0 8px' }}
              >
                {PART1_FIRST}
              </p>
            )}
            {recorder.state !== 'idle' && !reviewSignAll && (
              <p
                className={styles['fullWidth']}
                role={recorder.state === 'failed' ? 'alert' : 'status'}
                style={{
                  ...sx.description,
                  color: recorder.state === 'failed' ? '#c62828' : MUTED,
                  margin: '0 0 8px',
                }}
              >
                {recorder.state === 'starting'
                  ? RECORDING_STARTING
                  : RECORDING_REQUIRED + ' ' + recorder.reason}
              </p>
            )}
            {!reviewSignAll && !shownFinished && !part1Done && (
              <div
                className={styles['cardActions']}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  disabled={!ready || locked || recorder.state === 'starting'}
                  onClick={() => begin()}
                >
                  {!ready ? (
                    'Preparing agreement…'
                  ) : complete && finishHere ? (
                    'Finish'
                  ) : done ? (
                    <>
                      <span className={styles['desktopOnly']}>Start</span>
                      <span className={styles['mobileOnly']}>Continue</span>
                    </>
                  ) : (
                    'Start'
                  )}
                </Button>
                {detailsToggle}
                {canSignAll && ready && !locked && (
                  <Button
                    className={styles['detail']}
                    variant="outlined"
                    size="large"
                    onClick={() => setReviewSignAll(true)}
                  >
                    Sign all at once
                  </Button>
                )}
              </div>
            )}
          </section>
        )}
      </>
    )

  if (minimized)
    return (
      <button
        type="button"
        className="noPDF"
        aria-label="Continue signing"
        style={sx.bubble}
        onClick={continueSigning}
      >
        <span aria-hidden style={{ fontSize: 18 }}>
          ✍
        </span>
        {complete && finishHere
          ? 'All signed · Finish'
          : part1Done
          ? 'Part 1 complete · Continue'
          : 'Continue signing · ' + stepNumber + ' of ' + steps.length}
      </button>
    )

  return (
    <section
      className={'noPDF ' + styles['bar']}
      data-details={details || reviewSignAll}
      aria-label="Guided signing"
      style={sx.bar}
    >
      <div className={styles['barRow']} style={sx.row}>
        <div
          className={styles['barHeading']}
          style={{ minWidth: 220, flex: '1 1 320px' }}
        >
          <p style={sx.eyebrow}>
            {(partLabel || 'Sign your ' + partNoun) + ' · '}
            {partDone
              ? 'all ' + steps.length + ' done'
              : 'step ' + stepNumber + ' of ' + steps.length}
          </p>
          <p style={sx.stepTitle}>
            {complete && finishHere
              ? 'All signed — press Finish'
              : part1Done
              ? 'Part 1 complete'
              : next?.label}
          </p>
          <p id={detailsId} className={styles['detail']} style={sx.description}>
            {complete && finishHere
              ? 'We will email you a copy of the signed agreement.'
              : part1Done
              ? 'Continue with Part 2: the signatures in the agreement.'
              : next
              ? describeStep(next.field) +
                (nextSource
                  ? ' Click "Sign here" to place your signature.'
                  : part
                  ? ' Click the highlighted field to sign it.'
                  : ' Draw it once, then press Save.')
              : ''}
          </p>
        </div>
        <div
          className={styles['barActions']}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
        >
          {detailsToggle}
          <Button
            className={styles['detail']}
            variant="text"
            onClick={() => {
              closeSignatureDialog()
              setMinimized(true)
            }}
          >
            View agreement
          </Button>
          {canSignAll && !signAll && !reviewSignAll && (
            <Button
              className={styles['detail']}
              variant="outlined"
              onClick={() => setReviewSignAll(true)}
            >
              Sign all at once
            </Button>
          )}
          {next && nextSource && (
            <Button variant="contained" color="warning" onClick={placeNext}>
              <span className={styles['desktopOnly']}>
                Sign here: {next.label}
              </span>
              <span className={styles['mobileOnly']}>Sign here</span>
            </Button>
          )}
          {next && !nextSource && (
            <Button variant="contained" onClick={focusNext}>
              <span className={styles['desktopOnly']}>Next: {next.label}</span>
              <span className={styles['mobileOnly']}>Next</span>
            </Button>
          )}
          {part1Done && (
            <Button variant="contained" onClick={toPart2}>
              <span className={styles['desktopOnly']}>Continue to Part 2</span>
              <span className={styles['mobileOnly']}>Part 2</span>
            </Button>
          )}
          {finishHere && (
            <Button
              className={!complete ? styles['detail'] : undefined}
              variant="contained"
              color="success"
              disabled={
                !complete ||
                status === 'sending' ||
                status === 'sent' ||
                (!!receipt && !resend) ||
                !!receiptError
              }
              onClick={() => {
                setConfirm(true)
                setRecipient(email)
              }}
            >
              Finish
            </Button>
          )}
        </div>
      </div>
      <Progress steps={steps} next={next?.field} />
      {receiptBlock}
      {signAllReview}
      {emailForm}
    </section>
  )
}
